import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import papi, { formatErr } from "@/lib/partnerApi";
import PartnerNav from "@/pages/partner/PartnerNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, MessageSquareQuote, BadgeCheck, Eye, EyeOff, X } from "lucide-react";

const empty = {
  customer_name: "", initials: "", city: "", locality: "", customer_type: "Homeowner",
  review: "", project_type: "", display_order: 0, verified: false, published: true,
};

const autoInitials = (name) => {
  const parts = (name || "").replace("&", " ").split(/\s+/).filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase();
};

export default function AdminTestimonials() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // {..., id?}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await papi.get("/partner/testimonials");
      setRows(res.data);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) navigate("/login");
    } finally { setLoading(false); }
  };
  useEffect(() => {
    if (localStorage.getItem("partner_role") !== "admin") { navigate("/login"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => setForm({ ...empty, display_order: (rows.length + 1) });
  const openEdit = (r) => setForm({ ...r });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.customer_name.trim() || !form.review.trim()) { toast.error("Name and review are required"); return; }
    setSaving(true);
    const payload = {
      ...form,
      initials: form.initials.trim() || autoInitials(form.customer_name),
      display_order: Number(form.display_order) || 0,
    };
    delete payload.created_at;
    try {
      if (form.id) await papi.put(`/partner/testimonials/${form.id}`, payload);
      else await papi.post("/partner/testimonials", payload);
      toast.success(form.id ? "Testimonial updated" : "Testimonial added");
      setForm(null); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try { await papi.delete(`/partner/testimonials/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  if (loading) return <div className="min-h-screen bg-[#FBFAF9] flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#0E9AC8]" /></div>;

  return (
    <div className="min-h-screen bg-[#FBFAF9]">
      <PartnerNav />
      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
              <MessageSquareQuote className="h-6 w-6 text-[#0E9AC8]" /> Homepage Testimonials
            </h1>
            <p className="text-sm text-slate-500 mt-1">Manage the homeowner reviews shown on the homepage. Only mark "Verified" once a review is genuinely verified.</p>
          </div>
          <Button data-testid="add-testimonial" onClick={openNew} className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl shrink-0">
            <Plus className="h-4 w-4 mr-1.5" /> Add
          </Button>
        </div>

        <div className="mt-6 space-y-3">
          {rows.length === 0 && <p className="text-sm text-slate-400 py-10 text-center">No testimonials yet. Add the first one.</p>}
          {rows.map((r) => (
            <div key={r.id} data-testid={`testimonial-row-${r.id}`} className="card-premium rounded-2xl p-5 flex items-start gap-4">
              <div className="h-11 w-11 rounded-full bg-slate-900 text-white flex items-center justify-center font-semibold text-sm shrink-0">{r.initials}</div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-900 text-sm">{r.customer_name}</span>
                  <span className="text-xs text-slate-400">#{r.display_order}</span>
                  {r.verified
                    ? <span className="inline-flex items-center gap-1 rounded-full bg-[#0E9AC8]/10 text-[#0E9AC8] px-2 py-0.5 text-[11px] font-semibold"><BadgeCheck className="h-3 w-3" /> Verified</span>
                    : <span className="rounded-full bg-slate-100 text-slate-500 px-2 py-0.5 text-[11px] font-semibold">Illustrative</span>}
                  {r.published
                    ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-600 px-2 py-0.5 text-[11px] font-semibold"><Eye className="h-3 w-3" /> Published</span>
                    : <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-600 px-2 py-0.5 text-[11px] font-semibold"><EyeOff className="h-3 w-3" /> Draft</span>}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{r.customer_type} · {r.locality ? `${r.locality}, ` : ""}{r.city}{r.project_type ? ` · ${r.project_type}` : ""}</p>
                <p className="text-sm text-slate-600 mt-2 line-clamp-2">{r.review}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => openEdit(r)} data-testid={`edit-testimonial-${r.id}`} className="text-slate-400 hover:text-[#0E9AC8] p-1.5"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(r.id)} data-testid={`delete-testimonial-${r.id}`} className="text-slate-400 hover:text-red-600 p-1.5"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </main>

      {form && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setForm(null)}>
          <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="testimonial-form">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-slate-900">{form.id ? "Edit testimonial" : "New testimonial"}</h2>
              <button onClick={() => setForm(null)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <Label>Customer name *</Label>
                  <Input data-testid="tf-name" className="rounded-xl mt-1.5" value={form.customer_name}
                    onChange={(e) => set("customer_name", e.target.value)} placeholder="Amit & Neha Sharma" />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label>Initials</Label>
                  <Input data-testid="tf-initials" className="rounded-xl mt-1.5" value={form.initials} maxLength={3}
                    onChange={(e) => set("initials", e.target.value.toUpperCase())} placeholder={autoInitials(form.customer_name) || "AS"} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>City</Label>
                  <Input data-testid="tf-city" className="rounded-xl mt-1.5" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Noida" />
                </div>
                <div>
                  <Label>Locality</Label>
                  <Input data-testid="tf-locality" className="rounded-xl mt-1.5" value={form.locality} onChange={(e) => set("locality", e.target.value)} placeholder="Sector 150" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Customer type</Label>
                  <Input data-testid="tf-type" className="rounded-xl mt-1.5" value={form.customer_type} onChange={(e) => set("customer_type", e.target.value)} placeholder="Homeowner / Homeowners" />
                </div>
                <div>
                  <Label>Project type</Label>
                  <Input data-testid="tf-project" className="rounded-xl mt-1.5" value={form.project_type} onChange={(e) => set("project_type", e.target.value)} placeholder="New home / Renovation" />
                </div>
              </div>
              <div>
                <Label>Review *</Label>
                <Textarea data-testid="tf-review" className="rounded-xl mt-1.5 min-h-[120px]" value={form.review}
                  onChange={(e) => set("review", e.target.value)} placeholder="What the homeowner said…" />
              </div>
              <div>
                <Label>Display order</Label>
                <Input data-testid="tf-order" type="number" className="rounded-xl mt-1.5 w-32" value={form.display_order}
                  onChange={(e) => set("display_order", e.target.value)} />
              </div>
              <div className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
                <div>
                  <p className="text-sm font-medium text-slate-800">Verified Homeowner</p>
                  <p className="text-xs text-slate-400">Only enable if Nirmantra has actually verified this review.</p>
                </div>
                <Switch data-testid="tf-verified" checked={form.verified} onCheckedChange={(v) => set("verified", v)} />
              </div>
              <div className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
                <div>
                  <p className="text-sm font-medium text-slate-800">Published</p>
                  <p className="text-xs text-slate-400">Show this review on the homepage.</p>
                </div>
                <Switch data-testid="tf-published" checked={form.published} onCheckedChange={(v) => set("published", v)} />
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setForm(null)} className="rounded-xl">Cancel</Button>
              <Button data-testid="save-testimonial" onClick={save} disabled={saving} className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
